/**
 * The Intelligence tab (CT-5715): its subtabs, in order. Each is a CHILD ROUTE of
 * the tab (`intelligence/traits`), so a subtab is linkable and Back behaves.
 */
export const INTELLIGENCE_SUBTABS = Object.freeze(['traits', 'insights']);

export const DEFAULT_INTELLIGENCE_SUBTAB = 'traits';

/** The Traits chips, in the prototype's order. The id is what the request carries. */
export const TRAIT_QUICK_FILTERS = Object.freeze([
  'all',
  'role_in_deal',
  'deal',
  'interests',
  'communication',
  'personal',
]);

export const DEFAULT_TRAIT_QUICK_FILTER = 'all';

/**
 * NAMED orders, not field + direction: the server knows what "needs attention"
 * means. The prototype still shows a direction arrow, so the direction only
 * reverses the named order. The labels are product copy, hence i18n keys.
 */
export const TRAIT_SORTS = Object.freeze(['needs_attention', 'expires_soonest', 'newest_evidence']);
export const TRAIT_SORT_FIELD = 'needs_attention';
export const TRAIT_SORT_DIRECTION = 'asc';

/** What a search over the traits covers. */
export const TRAIT_SEARCH_FIELDS = Object.freeze(['title', 'quote']);

/**
 * The All filters dimensions. Unlike a tenant's LIST tab these are fixed product
 * dimensions, so they live here with i18n keys instead of arriving with the rows.
 */
export const TRAIT_FILTERS = Object.freeze([
  { id: 'source', options: ['noted', 'said'] },
  { id: 'status', options: ['re_confirm_due', 'active'] },
  { id: 'used_in', options: ['how_to_approach', 'next_best_action', 'drafts', 'not_used'] },
]);

/** The places a trait can be used. A trait used in neither drafts nor How to approach says so on its card. */
export const TRAIT_USES = Object.freeze(['how_to_approach', 'next_best_action', 'drafts']);

/**
 * A trait whose validity ends within this many days reads "Expiring soon".
 * ASSUMED (CT-5715, open question 8): the prototype shows it at 13 days.
 */
export const TRAIT_EXPIRING_SOON_DAYS = 14;

export const TRAITS_PAGE_SIZE_OPTIONS = Object.freeze([10, 15, 20, 50]);

/** The Insights chips (CT-5716), in the prototype's order. */
export const INSIGHT_QUICK_FILTERS = Object.freeze([
  'all',
  'channel',
  'timing',
  'how_they_decide',
  'deal_outlook',
  'scheduling',
]);

export const DEFAULT_INSIGHT_QUICK_FILTER = 'all';

/** Named orders, like the traits'. */
export const INSIGHT_SORTS = Object.freeze(['recently_changed', 'needs_attention', 'most_confident']);
export const INSIGHT_SORT_FIELD = 'recently_changed';
export const INSIGHT_SORT_DIRECTION = 'asc';

export const INSIGHT_SEARCH_FIELDS = Object.freeze(['title', 'headline']);

export const INSIGHT_FILTERS = Object.freeze([{ id: 'status', options: ['weakening', 'rising', 'stable', 'emerging'] }]);

/**
 * The `Chip` variant of each trend. ASSUMED (CT-5716, open question 3) from the
 * prototype's colours: amber weakening, green rising, blue stable, grey low data
 * and paused, purple emerging.
 */
export const INSIGHT_TREND_VARIANTS = Object.freeze({
  weakening: 'warning',
  rising: 'success',
  stable: 'informative',
  low_data: 'neutral',
  emerging: 'purple',
  paused: 'neutral',
});

/** The `Badge` variant of an insight's status in the slide-out: active, emerging (below the threshold) or paused. */
export const INSIGHT_STATUS_VARIANTS = Object.freeze({
  active: 'success',
  emerging: 'purple',
  paused: 'neutral',
});

/** How many sources the slide-out lists before "Show N more sources". */
export const INSIGHT_SOURCES_PREVIEW = 3;
